import type { TransactionItem } from '@/types/core-api';
import { newestFirst } from '../_shared/TransactionsList';

/** One payment the customer made, with the per-case rows it was split into. */
export interface PaymentGroup {
  key: string;
  amountMillicents: number;
  createdAt: string | null;
  splits: TransactionItem[];
}

/**
 * Names the debt when the payment landed in exactly one, else counts them. `debtNames` misses any
 * split whose case is not loaded, so the count doubles as the fallback.
 */
export function paymentGroupDebtLabel(
  group: PaymentGroup,
  debtNames: Map<string, string> | undefined,
  t: (key: string, options?: { count: number }) => string
): string {
  const named = group.splits.length === 1 ? debtNames?.get(group.splits[0].transaction_id) : undefined;

  return named ?? t('transaction.debtCount', { count: group.splits.length });
}

/**
 * A payment across an account is stored as one row per case, so the ledger shows amounts the
 * customer never entered. Group them back by the payment they came from — `payment_id` when the
 * provider gave one, else the row stands alone.
 */
export function groupByPayment(transactions: TransactionItem[]): PaymentGroup[] {
  const groups = new Map<string, TransactionItem[]>();

  for (const tx of newestFirst(transactions)) {
    const paymentId = tx.metadata?.payment_id;
    const key = typeof paymentId === 'string' && paymentId ? paymentId : tx.transaction_id;
    groups.set(key, [...(groups.get(key) ?? []), tx]);
  }

  return [...groups].map(([key, splits]) => ({
    key,
    amountMillicents: splits.reduce((total, tx) => total + tx.amount_millicents, 0),
    createdAt: splits[0].created_at,
    splits,
  }));
}
